import React, { useEffect } from 'react';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import { DeleteIcon, LocationsIcon, OrganizationsIcon, RobotIcon } from '@common/utils/icons/icons';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../../store';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import SearchBar from '../../ui/SearchBar';
import SearchType from '../../../models/enums/search';
import { isHttpError } from '@common/interfaces/api/http-error';
import { selectUser } from '../../../store/general/general-slice-selectors';
import SearchResult from '../../../models/misc/search-result';
import {
  postSubscribeToOrganizationById,
  postSubscribeToRobotById,
} from '../../../store/general/general-actions';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import { resetUserRestStatus } from '../../../store/general/general-slice';
import TooltipIconButton from '@common/components/Inputs/TooltipIconButton';
import { resetSearchRestStatus } from '../../../store/organization/organization-slice';
import { replaceArgsInMessage } from '../../../utils/strings';
import OrganizationRole from '../../../models/enums/organization-roles';
import CustomSplitColumnCard from '@common/components/misc/CustomSplitColumnCard';

const SubscribedLists: React.FC = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const userData = useAppSelector((state) => selectUser(state));

  useEffect(() => {
    if (
      userData.rest.postSubscribeOrganizationStatus === HttpFetchStatus.Success ||
      userData.rest.postSubscribeRobotStatus === HttpFetchStatus.Success
    ) {
      dispatch(resetSearchRestStatus());
      dispatch(
        resetUserRestStatus({
          postSubscribeOrganizationStatus: HttpFetchStatus.Idle,
          postSubscribeOrganizationError: null,
          postSubscribeRobotStatus: HttpFetchStatus.Idle,
          postSubscribeRobotError: null,
        }),
      );
    }
  }, [
    dispatch,
    userData.rest.postSubscribeOrganizationStatus,
    userData.rest.postSubscribeRobotStatus,
  ]);

  const subscribeItemClickHandler = (result: SearchResult) => {
    if (
      result.type === SearchType.ORGANIZATION &&
      !userData.data.organizationSubscriptions.some((org) => org.id === result.id)
    ) {
      dispatch(postSubscribeToOrganizationById(result.id));
    } else if (
      result.type === SearchType.ROBOT &&
      !userData.data.robotSubscriptions.some((robot) => robot.id === result.id)
    ) {
      dispatch(postSubscribeToRobotById(result.id));
    }
  };

  const deleteOrganizationSubscriptionClickHandler = (id: string) => {
    dispatch(postSubscribeToOrganizationById(id));
  };

  const deleteRobotSubscriptionClickHandler = (id: string) => {
    dispatch(postSubscribeToRobotById(id));
  };

  const closeAddSubscriptionHandler = () => {
    dispatch(resetSearchRestStatus());
  };

  return (
    <React.Fragment>
      <CustomSplitColumnCard
        title={t('title-subscriptions')}
        content={[
          {
            left: (
              <React.Fragment>
                {userData.rest.postSubscribeRobotError !== null &&
                  isHttpError(userData.rest.postSubscribeRobotError) && (
                    <CustomAlert
                      severity="error"
                      header={t(userData.rest.postSubscribeRobotError.message)}
                    />
                  )}
                {userData.rest.postSubscribeOrganizationError !== null &&
                  isHttpError(userData.rest.postSubscribeOrganizationError) && (
                    <CustomAlert
                      severity="error"
                      header={t(userData.rest.postSubscribeOrganizationError.message)}
                    />
                  )}
                <SearchBar
                  onItemClick={subscribeItemClickHandler}
                  onClose={closeAddSubscriptionHandler}
                  fields={[SearchType.ORGANIZATION, SearchType.ROBOT]}
                  fullSize
                  placeholder={t('input-label-search-subscription')}
                />
                <Typography variant="body2" p={2}>
                  {t('info-active-notification-subscriptions')}
                </Typography>
              </React.Fragment>
            ),
            right: (
              <React.Fragment>
                <Stack padding={2}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <OrganizationsIcon />
                    <Stack spacing={1}>
                      <Typography variant="h3">{t('title-organizations')}</Typography>
                    </Stack>
                  </Stack>
                  <Stack spacing={2} pl={2} pt={1}>
                    {userData.data.organizationSubscriptions.map((org) => (
                      <Stack
                        key={org.id}
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Stack direction="row" alignItems="center" spacing={1}>
                          {org.role === OrganizationRole.LOCATION ? (
                            <LocationsIcon color="action" />
                          ) : (
                            <OrganizationsIcon color="action" />
                          )}
                          <Stack>
                            <Typography variant="body2">{org.name}</Typography>
                            <Stack direction="row" spacing={2}>
                              <Tooltip
                                title={
                                  <Stack
                                    sx={(theme) => ({
                                      maxHeight: theme.spacing(24),
                                      overflow: 'auto',
                                      p: 1,
                                    })}
                                  >
                                    {org.robots.map((r) => (
                                      <Typography key={r.id} variant="inherit">
                                        {r.name}
                                      </Typography>
                                    ))}
                                  </Stack>
                                }
                              >
                                <Typography variant="caption">
                                  {replaceArgsInMessage(t('info-subscribed-robots'), [
                                    org.robots.length,
                                  ])}
                                </Typography>
                              </Tooltip>
                              {org.locations.length > 0 && (
                                <Tooltip
                                  title={
                                    <Stack
                                      sx={(theme) => ({
                                        maxHeight: theme.spacing(24),
                                        overflow: 'auto',
                                        p: 1,
                                      })}
                                    >
                                      {org.locations.map((l) => (
                                        <Typography key={l.id} variant="inherit">
                                          {l.name}
                                        </Typography>
                                      ))}
                                    </Stack>
                                  }
                                >
                                  <Typography variant="caption">
                                    {replaceArgsInMessage(t('info-subscribed-locations'), [
                                      org.locations.length,
                                    ])}
                                  </Typography>
                                </Tooltip>
                              )}
                            </Stack>
                          </Stack>
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <TooltipIconButton
                            tooltip={t('button-delete')}
                            icon={<DeleteIcon color="error" />}
                            onClick={() => deleteOrganizationSubscriptionClickHandler(org.id)}
                          />
                        </Stack>
                      </Stack>
                    ))}
                  </Stack>
                </Stack>

                <Stack padding={2}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <RobotIcon />
                    <Stack spacing={1}>
                      <Typography variant="h3">{t('title-robots')}</Typography>
                    </Stack>
                  </Stack>
                  <Stack spacing={2} pl={2} pt={1}>
                    {userData.data.robotSubscriptions.map((robot) => (
                      <Stack
                        key={robot.id}
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <RobotIcon color="action" />
                          <Stack>
                            <Typography variant="body2">{robot.name}</Typography>
                            <Typography variant="caption">{robot.organizationName}</Typography>
                          </Stack>
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <TooltipIconButton
                            tooltip={t('button-delete')}
                            icon={<DeleteIcon color="error" />}
                            onClick={() => deleteRobotSubscriptionClickHandler(robot.id)}
                          />
                        </Stack>
                      </Stack>
                    ))}
                  </Stack>
                </Stack>
              </React.Fragment>
            ),
          },
        ]}
      />
    </React.Fragment>
  );
};

export default SubscribedLists;
